/**
 * @fileoverview Module for `src/dashboard/manage-letters/BundleListItem.js`.
 */

import PropTypes from 'prop-types';
import LetterListItem from './LetterListItem';
import ManageColumnCell from './common/ManageColumnCell';
import DownloadButton from './common/DownloadButton';
import classNames from 'classnames';
import { useDownloadBundleState } from '../../lib/queries/queries';
import ManageSlideOut from './common/ManageSlideOut';
import StatusChangeButton from './common/StatusChangeButton';
import SocialBundleInfo from './SocialBundleInfo';
import { useState } from 'react';


/**
 * Renders one letter bundle row with status actions and optional letter details.
 *
 * @param {Object} params
 * @returns {JSX.Element} Bundle card content for a manage letters column.
 */
function BundleListItem({ bundle, modifyAndDownloadState, testLabel, canAdoptSocial, shouldShowLetterInfo, status, isReady }) {
  const downloadState = useDownloadBundleState({ letterBundleId: bundle.bundle_id, modifyAndDownloadState });
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  /**
   * Toggles the expanded/collapsed state of the bundle's letter detail card.
   */
  const toggleCardExpansion = () => {
    setIsCardExpanded(!isCardExpanded);
  };

  const statusContent = {
    UNPREPPED: {
      name: 'unprepared',
      buttonRowClassNames: 'row flex-nowrap unprepared',
    },
    PREPPED: {
      name: 'prepared',
      buttonRowClassNames: 'row',
    },
    SENT: {
      name: 'sent',
      buttonRowClassNames: 'row bundle-status-row',
    },
  };

  const letterKitType = bundle.kit_type === 'FULL' ? 'Letter Kit' : 'Stamp Subsidy';
  const letterQuantityMessage = `${ bundle.letter_count }/${ bundle.total_bundle_size } ${ statusContent[status].name } letters`;

  return (
    <ManageColumnCell bundleData={ bundle } canAdoptSocial={ canAdoptSocial } cellId={ bundle.hashid } isReady={ isReady } testLabel={ testLabel }>
      { bundle.type === 'LETTERKIT' && (
        <div className='row fill-row'>
          <div className='col fw-bold fill-row'>
            { (bundle.letter_kit_status === 'REQUESTED') && (
              <span>
                { `${ letterKitType } order: Pending approval (${ bundle.letter_count } letters)` }
              </span>
            ) }
            { (bundle.letter_kit_status === 'APPROVED') && (
              <span>
                { `${ letterKitType } order: Not yet shipped` }
              </span>
            ) }
            { bundle.letter_kit_status === 'SENT' && bundle.usps_tracking_code && (
              <div> <span>{ letterKitType } order: </span>
                <a href={ `https://tools.usps.com/go/TrackConfirmAction?tLabels=${ bundle.usps_tracking_code }` } rel='noreferrer' target='_blank'>In transit</a>
              </div>
            ) }
            { bundle.letter_kit_status === 'SENT' && !bundle.usps_tracking_code && (<span>Shipped</span>) }

          </div>
        </div>
      ) }

      { bundle.letter_kit_status !== 'REQUESTED' && (
        <div>
          <div className={ classNames(statusContent[status].buttonRowClassNames) }>
            { status === 'UNPREPPED' && (
              <DownloadButton
                buttonType='columnCell'
                downloadState={ downloadState }
                isReady={ isReady }
                modifyAndDownloadState={ modifyAndDownloadState }
              />
            ) }
            <StatusChangeButton
              buttonType='columnCell'
              isBackwardMutation
              isReady={ isReady }
              lettersToMutate={ bundle.letters }
              modifyAndDownloadState={ modifyAndDownloadState }
              segmentTrackPropeties={{ bundleId: bundle.bundle_id, previousState: statusContent[status].name }}
              status={ status }
            />
            <StatusChangeButton
              buttonType='columnCell'
              isReady={ isReady }
              lettersToMutate={ bundle.letters }
              mailOnDate={ bundle.mail_start_date }
              modifyAndDownloadState={ modifyAndDownloadState }
              segmentTrackPropeties={{ bundleId: bundle.bundle_id, previousState: statusContent[status].name }}
              status={ status }
            />
            { status === 'SENT'
              && (
                <div className='col'>
                  { /* TODO: implement on-click */ }
                  <div className='mailed-message fw-bold w-100'>
                    <i className='fa fa-check-circle' />
                    <span>Nice work!</span>
                  </div>
                </div>
              ) }
          </div>
          {
            shouldShowLetterInfo(bundle) && (
              bundle.social_token
                ? (<SocialBundleInfo bundle={ bundle } myLettersMessage={ letterQuantityMessage } />)
                : (
                  <ManageSlideOut
                    isCardExpanded={ isCardExpanded }
                    items={ bundle.letters }
                    renderLineItem={ (letter) => (
                      <LetterListItem
                        isReady={ isReady }
                        key={ letter.hashid || letter.id }
                        letter={ letter }
                        mailOnDate={ bundle.mail_start_date }
                        modifyAndDownloadState={ modifyAndDownloadState }
                        status={ status }
                      />
                    ) }
                    slideOutId={ `${ bundle.hashid }-${ status }` }
                    toggleCardExpansion={ toggleCardExpansion }
                    viewLabel={ `View ${ letterQuantityMessage }` }
                  />
                  )
            )
          }
        </div>
      ) }
    </ManageColumnCell>
  );
}

BundleListItem.propTypes = {
  bundle: PropTypes.object.isRequired,
  canAdoptSocial: PropTypes.bool.isRequired,
  isReady: PropTypes.bool.isRequired,
  modifyAndDownloadState: PropTypes.object.isRequired,
  shouldShowLetterInfo: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  testLabel: PropTypes.string.isRequired,
};

export default BundleListItem;
